<template lang="pug">
//- A ticket's own files, wherever a ticket is edited: the detail form and the
//- panel behind the running timer. One implementation, because a screenshot
//- dropped on a call has to look and behave the same as one dropped on the form.
.ticket-files-field
    //- The same chip the email cards use for their attachments — a stored file
    //- looks the same wherever it is listed — with the dropzone tile carrying
    //- the library as a second source, which is where this pattern already
    //- lives in the product media field.
    attachment-upload(v-if="addable" dropzone visibility="private" @attached="addFile")
        button.ticket-files__library(type="button" @click.stop.prevent="fileLibraryVisible = true")
            i.el-icon-picture-outline
            span {{ $t('Select from library') }}
    image-library-dialog(:visible.sync="fileLibraryVisible" selection-type="file" @select="onLibrarySelect")

    //- Tiles rather than a list of filenames: a support ticket's files are
    //- mostly screenshots, and a screenshot you cannot see is a filename. An
    //- image opens the gallery over ALL of the ticket's images, so a series of
    //- them is paged through rather than opened one browser tab at a time;
    //- anything else keeps the plain link it always had.
    .ticket-files(v-if="files.length")
        .ticket-files__tile(v-for="file in files" :key="file.id")
            button.ticket-files__open(
                v-if="isImageFile(file)"
                type="button"
                :title="fileName(file)"
                @click="openFileGallery(file)"
            )
                img.ticket-files__image(:src="fileUrl(file)" :alt="fileName(file)" loading="lazy")
            a.ticket-files__open(
                v-else
                :href="fileUrl(file)"
                target="_blank"
                rel="noopener"
                :title="fileName(file)"
            )
                //- No picture to show, so say what it is. The type icon went:
                //- FontAwesome's own file-csv glyph already draws the word.
                span.ticket-files__type {{ fileExtension(file) }}
            .ticket-files__meta
                span.ticket-files__name {{ fileName(file) }}
                //- A file dropped a moment ago has no upload time until the
                //- ticket is saved.
                span.ticket-files__date(v-if="file.created_at") {{ formatDateTime(file.created_at) }}
                span.ticket-files__date(v-else-if="file.size") {{ parseInt(file.size) | prettyBytes }}
            //- Saving goes through the ticket, so the history says who took a
            //- copy; a file that is not on the saved ticket yet has no such
            //- route, and needs none: it came from this browser.
            el-button.ticket-files__download(
                v-if="isDownloadable(file)"
                circle
                type="primary"
                size="mini"
                icon="el-icon-fa-download fas"
                :title="$t('Download file')"
                @click.prevent.stop="downloadFile(file)"
            )
            //- The media field's own control, verbatim: a circular danger button
            //- with the trash icon, revealed on hover. Every other tile in the
            //- admin is cleared this way.
            el-button.ticket-files__remove(
                v-if="removable"
                circle
                type="danger"
                size="mini"
                icon="el-icon-fa-trash-alt far"
                :title="$t('Remove file')"
                @click.prevent.stop="removeFile(file.id)"
            )
    p.help(v-if="showHint") {{ $t('Files belong to the ticket. Files sent in the correspondence stay with their message.') }}

    //- Full size viewing through the shared PhotoSwipe gallery - the same
    //- viewer the media library and the conversation attachments use.
    photo-swipe-gallery(
        :images="lightbox.images"
        :index.sync="lightbox.index"
        :open.sync="lightbox.open"
    )
</template>

<i18n>
{
    "de": {
        "Select from library": "Aus Mediathek wählen",
        "Remove file": "Datei entfernen",
        "Download file": "Datei herunterladen",
        "Files belong to the ticket. Files sent in the correspondence stay with their message.": "Dateien gehören zum Ticket. In der Korrespondenz versendete Dateien bleiben bei ihrer Nachricht."
    }
}
</i18n>

<script>
import _ from 'lodash';

import ImageLibraryDialog from '@/components/Page/Sections/Partials/ImageLibraryDialog';
import AttachmentUpload from '@/extensions/communication/views/Conversation/Partials/AttachmentUpload';
import PhotoSwipeGallery from '@/components/PhotoSwipeGallery';
import { ensureFreshToken } from '@/helpers';
import { fileExtension, fileName, fileUrl, isImageFile, loadImageDimensions, saveFromUrl, ticketFileDownloadUrl } from '@/helpers/files';
import { timestamp2date } from '@/filters';

export default {
    name: 'TicketFilesField',
    components: {
        AttachmentUpload,
        ImageLibraryDialog,
        PhotoSwipeGallery,
    },
    model: {
        prop: 'files',
        event: 'change',
    },
    props: {
        files: {
            type: Array,
            default: () => [],
        },
        // The saved ticket, whose files can be downloaded through it; null
        // while the ticket is still being created.
        ticket: {
            type: Object,
            default: null,
        },
        // Off where files may only be added: a CMS admin on a ticket of
        // our desk (PlatformTicketsDrawer).
        removable: {
            type: Boolean,
            default: true,
        },
        // Off where the viewer may not change the ticket at all.
        addable: {
            type: Boolean,
            default: true,
        },
        // Off where the reader has no correspondence of their own to tell
        // apart (PlatformTicketsDrawer).
        showHint: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            fileLibraryVisible: false,
            lightbox: {
                open: false,
                index: 0,
                images: [],
            },
            // Measured once per file, because PhotoSwipe asks every time.
            fileDimensions: {},
        };
    },
    methods: {
        isImageFile,
        fileExtension,
        fileName,
        fileUrl,
        isDownloadable(file) {
            return !!this.ticket && (_.get(this.ticket, 'files') || []).some((saved) => saved.id === file.id);
        },
        async downloadFile(file) {
            await ensureFreshToken();
            saveFromUrl(ticketFileDownloadUrl(this.ticket.id, file));
        },
        formatDateTime(timestamp) {
            return timestamp2date(timestamp, 'dd.MM.yyyy, HH:mm');
        },
        addFile(file) {
            if (this.files.some((existing) => existing.id === file.id)) return;

            this.$emit('change', [...this.files, file]);
        },
        onLibrarySelect(selection) {
            const file = Array.isArray(selection) ? selection[0] : selection;
            if (!file || !file.id) return;

            this.addFile({ ...file, upload: false });
        },
        removeFile(id) {
            this.$emit(
                'change',
                this.files.filter((file) => file.id !== id)
            );
        },
        /**
         * Open the gallery on this image, with every OTHER image of the ticket
         * behind it - three screenshots of one bug are read by paging through
         * them, not by opening three tabs.
         */
        async openFileGallery(file) {
            // The gallery's download link carries the token in its href.
            await ensureFreshToken();
            const images = this.files.filter((entry) => isImageFile(entry));
            const index = images.findIndex((entry) => entry.id === file.id);

            this.lightbox.images = await Promise.all(images.map((entry) => this.buildGalleryImage(entry)));
            this.lightbox.index = Math.max(index, 0);
            this.lightbox.open = true;
        },
        // PhotoSwipe needs the real pixel size up front or it cannot zoom; the
        // File type carries none, so it is measured once and remembered.
        async buildGalleryImage(file) {
            const source = fileUrl(file);
            const id = String(file.id);
            let dimensions = this.fileDimensions[id];

            if (!dimensions) {
                try {
                    dimensions = await loadImageDimensions(source);
                    this.$set(this.fileDimensions, id, dimensions);
                } catch (error) {
                    dimensions = null;
                }
            }

            return {
                src: source,
                width: _.get(dimensions, 'width') || 1200,
                height: _.get(dimensions, 'height') || 800,
                alt: fileName(file),
                downloadUrl: this.isDownloadable(file) ? ticketFileDownloadUrl(this.ticket.id, file) : null,
            };
        },
    },
};
</script>

<style lang="sass" scoped>
// A contact sheet of what was sent in, rather than a grid of cards: the
// screenshot is what an agent is after, so a preview carries no frame and no
// divider of its own. Grey ground and cover-fit are the media library's, so a
// file looks the same in both places.
//
// The explicit line-height is not decoration: el-form-item__content sets 40px,
// and the caption inherited it - 11px text spaced like form fields, taller than
// the picture above it.
.ticket-files
    display: flex
    flex-wrap: wrap
    gap: 12px
    margin: 8px 0 6px
    line-height: 1.3

.ticket-files__tile
    position: relative
    width: 148px

    &:hover,
    &:focus-within
        .ticket-files__remove,
        .ticket-files__download
            opacity: 1

.ticket-files__open
    display: block
    width: 100%
    padding: 0
    border: 0
    border-radius: 3px
    aspect-ratio: 16 / 10
    overflow: hidden
    background: #f3f5f8
    // The whole tile is a link for a document, and the global link rule would
    // underline the type inside it.
    text-decoration: none
    cursor: pointer

    &:focus-visible
        outline: 2px solid #409EFF
        outline-offset: 1px

.ticket-files__image
    width: 100%
    height: 100%
    display: block
    object-fit: cover

// Nothing to show, so it says what it is. The filename below is truncated
// exactly where the extension would be.
.ticket-files__type
    display: flex
    align-items: center
    justify-content: center
    width: 100%
    height: 100%
    font-size: .78rem
    font-weight: 600
    color: #a8abb2

.ticket-files__meta
    padding: 4px 1px 0

.ticket-files__name
    display: block
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap
    font-size: .72rem
    color: #606266

.ticket-files__date
    display: block
    font-size: .66rem
    color: #a8abb2

// ImageField's placement for the same control: over the preview's top right
// corner, out of sight until the tile is hovered or focused. Download sits
// to the left of remove, the same size, so the pair reads as one row.
.el-button.ticket-files__remove,
.el-button.ticket-files__download
    position: absolute
    top: 5px
    right: 5px
    z-index: 2
    width: 24px
    height: 24px
    padding: 0
    opacity: 0
    transition: opacity .15s ease

    &:focus-visible
        opacity: 1

.el-button.ticket-files__download
    // Its own 8px away from remove: two filled circles touching read as one blob.
    right: 37px
    // Two el-buttons in a row get a left margin from Element; here they are
    // positioned, so the margin would only shift the second one.
    margin-left: 0

// The library entry point, inside the dropzone — the product media field's own
// control, in the admin's blue rather than the shop purple.
.ticket-files__library
    display: inline-flex
    align-items: center
    // The dragger is a stretching flex column, so an inline-flex child still
    // spans the full width without this.
    align-self: center
    gap: 6px
    margin-top: 12px
    padding: 4px 12px
    border: 1px solid #dcdfe6
    border-radius: 4px
    background: #ffffff
    font-size: 12px
    line-height: 1.2
    color: #409EFF
    cursor: pointer
    transition: border-color .15s ease, background-color .15s ease

    &:hover
        border-color: #409EFF
        background-color: rgba(64, 158, 255, .06)
</style>
